import Axios from "axios";
import { AirspaceType, IcaoClass } from "../tools/Airspaces";

const URL_BASE = 'https://api3.private-radar.com/openaip';
const KEYWORD_FRAME = '{FRAME}';
const KEYWORD_ZOOM = '{ZOOM}';
const URL_GET_AIRSPACES = `${URL_BASE}/Airspace/GetAirspaces?frame=${KEYWORD_FRAME}&zoom=${KEYWORD_ZOOM}`;

export default class AirspaceLayer {
    constructor(map, type, show) {
        this.map = map;
        this.show = show || false;
        this.type = type; // A...D, DANGER, PROHIBITED, RESTRICTED
        this.showTooltip = false;
        this.showLabels = false;
        this.airspaces = []; // List of airspaces received 
        this.geometry = []; // list of {Id:string, Id of the airspace , polyline:google.maps.polyline, polygon:google.maps.polygon}
        this.timestamp = 0;
        this.firstLoad = true;
        this.getSettingsFromLocalStorage();
        this.eventListener();
    }

    // Get settings from LS so we initialize the map with the last settings applied
    getSettingsFromLocalStorage() {

        // Get settings from localStorage
        let settings = JSON.parse(localStorage.getItem('mapsettings'));

        // If mapopts is not null
        if (settings === null)
            return;

        if (this.type === 'A')
            this.show = settings.showClassA;
        if (this.type === 'B')
            this.show = settings.showClassB;
        if (this.type === 'C')
            this.show = settings.showClassC;
        if (this.type === 'D')
            this.show = settings.showClassD;
        if (this.type === 'DANGER')
            this.show = settings.showDanger;
        if (this.type === 'RESTRICTED')
            this.show = settings.showRestricted;
        if (this.type === 'PROHIBITED')
            this.show = settings.showProhibited;
    }

    eventListener() {
        window.addEventListener('newMapOpts', (e) => {
            if (e.detail === null)
                return;

            if (this.type === 'A')
                this.show = e.detail.showClassA;
            if (this.type === 'B')
                this.show = e.detail.showClassB;
            if (this.type === 'C')
                this.show = e.detail.showClassC;
            if (this.type === 'D')
                this.show = e.detail.showClassD;
            if (this.type === 'DANGER')
                this.show = e.detail.showDanger;
            if (this.type === 'RESTRICTED')
                this.show = e.detail.showRestricted;
            if (this.type === 'PROHIBITED')
                this.show = e.detail.showProhibited;

            if (!this.show)
                this.removeAllAirspaces();
            else
                this.updateAirspaces();
        })
    }

    static async loadAirspaces(frame, zoom) {
        // Get
        let url = URL_GET_AIRSPACES.replace(KEYWORD_FRAME, frame).replace(KEYWORD_ZOOM, zoom);
		const res = await Axios.get(url);
        // console.log("loadAirspaces", res);

        // Error
        if (res.status !== 200 ){
            console.error('loadAirspaces error ' + res.error);
            return [];
        }

        // Success
        return res.data;
    }

    // Update airspaces
    updateAirspaces(airspaces) {
        // If we receive airspaces, update this.airspaces
        if (airspaces) {
            // console.log(`Airspace ${this.type} received new airspaces:`, airspaces);
            this.airspaces = airspaces;
        }

        // Remove the geometry of airspaces not in the list received
        this.removeGeometryNotReceived();
        
        // Add the new airspaces we received (we dont do anything for the others)
        let idsInGeometry = this.geometry.map(x => x._id);
        for(const a of this.airspaces) {
            // If we don't find the airspace in polygon or line we add it
            let idx = idsInGeometry.indexOf(a._id);
            if (idx === -1) 
                this.addAirspace(a);
        }
    }

    // IF NOT, CHECK IF AIRSPACES IN this.geometry ARE IN THIS.JSON
    removeGeometryNotReceived() {
        // Get the list of ids received
        let idsReceived = this.airspaces.map(x => x._id);

        // Remove all the polygons and lines not in the ids
        for(let i=0; i<this.geometry.length; i++) {
            let geom = this.geometry[i];

            // If the geometry is in the ids received continue
            let idx = idsReceived.indexOf(geom._id);

            // Found => continue
            if (idx !== -1) {
                // console.log(`${geom._id} already in geometry => do not add`)
                continue;
            }
        
            // Not found => hide and remove from geometry
            // console.log(`${geom._id} not in airspaces received => remove`)
            this.removeGeometry(i);
        }
    }

    removeGeometry(index) {
        // hide and remove polygon
        this.geometry[index].polygon.setMap(null);

        // hide and remove line
        this.geometry[index].line.setMap(null);
        
        // Remove from geometry
        this.geometry.splice(index, 1);
    }

    addAirspace(airspace) {
        if (!this.show)
            return;
        // console.log(`Add airspace ${airspace._id} to geometry`)

        // Shape settings
        let settings = this.getPolylineSettings(airspace);

        // Polygon
        let polygon = new window.google.maps.Polygon(settings);
        polygon.setVisible(this.show); // this.show is always true here

        // Line
        let line = new window.google.maps.Polyline(settings);
        line.setVisible(this.show);

        // Add to geometry
        this.addToGeometry(airspace._id, polygon, line);

        // Add tooltips
        this.addTooltipsEvents(polygon, airspace);
    }

    addToGeometry(id, polygon, line) {
        this.geometry.push({
            _id : id,
            polygon,
            line
        });
    }

    addTooltipsEvents(polygon, airspace) {
        // Mouseover
        window.google.maps.event.addListener(polygon, 'mouseover', (e) => {
            let ev = new CustomEvent('airspaceInfoWindowOpen', {
                detail: airspace
            });
            window.dispatchEvent(ev);
        });
        
        // Mouseout
        window.google.maps.event.addListener(polygon, 'mouseout', (e) => {
            let ev = new CustomEvent('airspaceInfoWindowClose', {
                detail: airspace
            });
            window.dispatchEvent(ev);
        });
        
        // Click
        window.google.maps.event.addListener(polygon, 'click', (e) => {
            let ev = new CustomEvent('airspaceClick', {
                detail: {
                    lat: e.latLng.lat(),
                    lon: e.latLng.lng(),
                }
            });
            window.dispatchEvent(ev);
        })
    }

    // ADD AIRSPACE

    getPolylineSettings(airspace) {
        // Get the color for this airspace category
        let color = AirspaceLayer.getAirspaceColor(airspace);

        // Create settings object 
        let settings = {
            path: this.getPath(airspace.coordinates),
            geodesic: true,
            strokeColor: color,
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: color,
            fillOpacity: 0.06,
            icons: [{
                    icon: this.getIconBorder(color),
                    offset: '0',
                    repeat: '1px'
                },
                {
                    icon: this.getIconInside(color),
                    offset: '0',
                    repeat: '1px'
                }
            ],
            map: this.map
        };
        
        switch(this.type) {
            case 'RESTRICTED':
            case 'DANGER':
                settings = this.getSettingsWithIconsPath(settings, color);
                break;
            case 'PROHIBITED':
                settings.fillOpacity = 0.15;
                break;
            default: // To avoid warning
                break;
        }
        
        // Return settings
        // console.log(settings)
        return settings;
    }

    getPath(coordinates) {
        let path = coordinates.map((c, i) => {
            return {
                lat: c[1], // Attention lat is the 2nd coordinate!
                lng: c[0]
            };
        });
        // console.log(path);
        return path;
    }

    static getAirspaceColor(airspace) {
        // ICAO class
        switch(airspace.icaoClass) {
            case IcaoClass.A:
                return 'red';
            case IcaoClass.B:
                return '#34a834';
            case IcaoClass.C:
                return '#00F';
            case IcaoClass.D:
                return '#d602ee';
            default: // To avoid warning
                break;
        }

        // Airspace type
        switch(airspace.type) {
            case AirspaceType.Danger:
                return '#D50000';
            case AirspaceType.Restricted:
                return '#6002ee';
            case AirspaceType.Prohibited:
                return '#d87079';
            default: // To avoid warning
                return '';
        }
    }

    /**
     * Returns A,...D, P for Prohibited, R for Restricted, DG for Danger
     * @param {*} airspace 
     * @returns 
     */
    static getAirspaceClassShort(airspace) {
        // ICAO class
        switch(airspace.icaoClass) {
            case IcaoClass.A:
                return 'A';
            case IcaoClass.B:
                return 'B';
            case IcaoClass.C:
                return 'C';
            case IcaoClass.D:
                return 'D';
            default: // To avoid warning
                break;
        }

        // Airspace type
        switch(airspace.type) {
            case AirspaceType.Danger:
                return 'D';
            case AirspaceType.Restricted:
                return 'R';
            case AirspaceType.Prohibited:
                return 'P';
            default: // To avoid warning
                return '';
        }
    }
    getIconInside(color) {
        return {
            path: 'M 5,0 5,1',
            strokeOpacity: 0.3,
            strokeWeight: 7,
            strokeColor: color,
            scale: 1
        };
    }

    getIconBorder(color) {
        return {
            path: 'M 0,0 0,1',
            strokeOpacity: 1,
            strokeWeight: 2,
            strokeColor: color,
            scale: 1
        };
    }

    getSettingsWithIconsPath(settings, color){
        // Border
        settings.icons[0].icon = {
            path: 'M 0,0 0,1',
            strokeOpacity: 1,
            strokeColor: color,
            strokeWeight: 2,
            scale: 1
        };

        // Inside
        settings.icons[1].icon = {
            path: 'M 0,0 5,3',
            strokeOpacity: 1,
            strokeWeight: 1,
            strokeColor: color,
            scale: 1
        };
        settings.icons[1].repeat = '5px';
        return settings;
    }

    removeAllAirspaces() {
        // Remove airspaces from map
        for (let i=0; i<this.geometry.length; i++) {
            this.geometry[i].polygon.setMap(null);
            this.geometry[i].line.setMap(null);
        }
        // Reset geometry
        this.geometry = [];
    }
}